import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { tap, throwError } from 'rxjs';
import { Environment } from '../../environment';
import { isServerUnavailableError } from '../../http/http-error.service';
import { NotificationService } from '../../notification/notification.service';
import { AuthenticationService } from '../authentication.service';

@Component({
    selector: 'lib-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
    private destroyRef = inject(DestroyRef);

    loginForm: UntypedFormGroup;
    private params: Params;

    private checkClosedTimer;
    authWindow: Window;

    canAuthenticateWithAD: boolean = Environment.adAuthentication;

    constructor(
        private authenticationService: AuthenticationService,
        private fb: UntypedFormBuilder,
        private router: Router,
        private activateRoute: ActivatedRoute,
        private notificationService: NotificationService,
    ) {}

    ngOnInit() {
        this.loginForm = this.fb.group({
            email: ['', Validators.required],
            password: ['', [Validators.required, Validators.minLength(4)]],
        });

        this.activateRoute.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => (this.params = params));
    }

    login() {
        if (!this.loginForm.valid) {
            return;
        }
        this.notificationService.notify('Connexion en cours...');
        this.authenticationService
            .login(this.loginForm.value)
            .pipe(tap(() => this.notificationService.dismiss()))
            .subscribe({
                next: () => {
                    const url = this.params.linkUrl || Environment.defaultLandingPage;
                    this.router.navigate([url]);
                },
                error: (error) => {
                    const realError = Object.hasOwn(error, 'errorResponse') ? error['errorResponse'] : error;
                    if (isServerUnavailableError(realError)) return throwError(() => Object.assign(new Error(), error));

                    const message: string =
                        realError?.status === 401
                            ? realError.error.detail.replace('Les identifications sont erronées', 'Les identifiants sont erronés') // Remplace le message de Spring ;-)
                            : 'Impossible de vous connecter. Merci de vérifier vos identifiants et votre connection internet ou de réessayer ultérieurement';
                    this.notificationService.error(message);
                },
            });
    }

    createLoginWindow() {
        this.authWindow = window.open(this.authenticationService.adLoginPath, '_blank', 'width=600,height=600');
        this.checkClosedTimer = setInterval(() => {
            if (this.authWindow.closed) {
                this.clearAuthWindow();
                this.notificationService.error("L'authentification a échoué");
            }
        }, 2000);

        window.addEventListener('message', this.messageListener);
    }

    messageListener = (event: MessageEvent<string>) => {
        if (
            (Environment.development && event.origin !== 'http://localhost:8082') ||
            (!Environment.development && event.origin !== Environment.host)
        ) {
            this.notificationService.error("Le message d'authentification n'a pas été envoyé par une adresse connue.");
        } else {
            switch (event.data) {
                case 'AUTH_SUCCESS':
                    console.log('Authentication successful!');
                    this.connectUser();
                    break;
                case 'USER_UNKNOWN':
                    this.notificationService.error('Utilisateur inconnu. Contactez votre responsable.');
                    break;
                default:
                    this.notificationService.error("Erreur dans le processus d'authentification. Contactez le SAV.");
                    console.error('Message : ', event.data);
            }
        }

        this.clearAuthWindow();
    };

    clearAuthWindow() {
        clearInterval(this.checkClosedTimer);
        window.removeEventListener('message', this.messageListener);
        this.authWindow.close();
    }

    connectUser() {
        this.authenticationService.queryAndSetCurrentUser(true).subscribe((user) => {
            if (user == null) {
                this.notificationService.error("L'authentification a échoué");
                return;
            }
            this.notificationService.dismiss();
            const url = this.params.linkUrl || Environment.defaultLandingPage;
            this.router.navigate([url]);
        });
    }
}
